import React from "react";
import { Text, StyleSheet } from "react-native";
import { Svg, Path, Circle } from "react-native-svg";
import { fontFamily } from "@utils/styles/fonts";

// Dark store visual spec, taken value-for-value from the product mockup
// "Multi-EDD Racking & Routing": §9 racking (frames 01–04) and §10 routing
// (frames 06–11). Shared by Receiving and DsRouting.
export const C = {
  page: "#F8F8F8",
  surface: "#FFFFFF",
  ink: "#1A1A1A",
  muted: "#8A8A8E",
  border: "#E6E6E9",
  red: "#C90643", // brand: primary CTA, selected card, "N to route"
  chipRed: "#C52022",
  chipRedBg: "#FCE8EC",
  green: "#187F53",
  greenBg: "#E8F5EE",
  blue: "#2F55C4", // DB name, PICKED BY DB
  blueLine: "#4775E6", // Change / Recommended route outline
  blueBg: "#EEF3FF",
  disabledBg: "#EFEFF1",
  disabledInk: "#B4B4BA",
  track: "#F0F0F2",
  avatar: "#E9E9EC",
  amber: "#92400E",
  amberBg: "#FEF3C7",
  pending: "#8A5D00", // OFD PENDING; Advance / TO BE RACKED text
  pendingBg: "#FFF4E5",
  orange: "#FBA410", // advance progress bar, advance banner
  orangeBg: "#FFF7E8",
  redBg: "#FDECEF", // PENDING / OVERDUE pill, pending banner
  redText: "#B0203B",
  searchIcon: "#9A9AA0",
  scrim: "rgba(0,0,0,0.45)",
};

// The mockup's weights (700 / 600 / 500) mapped to real Noto Sans files, so
// Android never fakes bold.
const FAMILY = {
  700: fontFamily.bold,
  600: fontFamily.semiBold,
  500: fontFamily.medium,
  400: fontFamily.regular,
};

/** Text at a mockup size/weight/colour. */
export const RText = ({
  size = 13,
  weight = 500,
  color = C.ink,
  style,
  ...rest
}) => (
  <Text
    allowFontScaling={false}
    includeFontPadding={false}
    textBreakStrategy="simple"
    {...rest}
    style={[
      {
        fontFamily: FAMILY[weight] || FAMILY[500],
        fontSize: size,
        color,
        lineHeight: Math.round(size * 1.4),
      },
      style,
    ]}
  />
);

const stroke = (color, width) => ({
  fill: "none",
  stroke: color,
  strokeWidth: width,
  strokeLinecap: "round",
  strokeLinejoin: "round",
});

export const ChevronRight = ({ size = 15, color = C.muted, width = 2.4 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M9 6l6 6-6 6" {...stroke(color, width)} />
  </Svg>
);

export const ChevronDown = ({ size = 14, color = C.muted }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M6 9l6 6 6-6" {...stroke(color, 2.4)} />
  </Svg>
);

export const Pin = ({ size = 15, color = C.red }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11z"
      {...stroke(color, 2)}
    />
    <Circle cx="12" cy="10" r="2.5" {...stroke(color, 2)} />
  </Svg>
);

export const RouteIcon = ({ size = 16, color = C.blueLine }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="6" cy="19" r="2" {...stroke(color, 2)} />
    <Circle cx="18" cy="5" r="2" {...stroke(color, 2)} />
    <Path d="M8 19h7a4 4 0 0 0 0-8H9a4 4 0 0 1 0-8h3" {...stroke(color, 2)} />
  </Svg>
);

export const PeopleIcon = ({ size = 24, color = C.blueLine }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M20 21v-2a4 4 0 0 0-3-3.87" {...stroke(color, 2)} />
    <Path d="M4 21v-2a4 4 0 0 1 3-3.87" {...stroke(color, 2)} />
    <Circle cx="12" cy="7" r="4" {...stroke(color, 2)} />
  </Svg>
);

export const Search = ({ size = 18, color = C.searchIcon }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="11" cy="11" r="7" {...stroke(color, 2.2)} />
    <Path d="M20 20l-3.2-3.2" {...stroke(color, 2.2)} />
  </Svg>
);

export const Warning = ({ size = 20, color = C.orange }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
      {...stroke(color, 2)}
    />
    <Path d="M12 9v4" {...stroke(color, 2)} />
    <Path d="M12 17h.01" {...stroke(color, 2)} />
  </Svg>
);

export const Check = ({ size = 14, color = "#FFFFFF" }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M20 6L9 17l-5-5" {...stroke(color, 3.2)} />
  </Svg>
);

/** The mockup's small status pill (9px / 600, 4×9 padding). */
export const Tag = ({ label, bg, color, size = 9, weight = 600, style }) => (
  <Text
    allowFontScaling={false}
    includeFontPadding={false}
    style={[
      tagStyles.tag,
      {
        backgroundColor: bg,
        color,
        fontSize: size,
        fontFamily: FAMILY[weight] || FAMILY[600],
      },
      style,
    ]}
  >
    {label}
  </Text>
);

const tagStyles = StyleSheet.create({
  tag: {
    fontFamily: FAMILY[600],
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
    overflow: "hidden",
    alignSelf: "flex-start",
  },
});

/** A card's title: the customer's name, or "TP - id" without one. */
export const tpName = (tp) => {
  const name = tp && tp.cx_name ? String(tp.cx_name).trim() : "";
  return name || `TP - ${tp?.cx_id ?? ""}`;
};

/** "TP - id" for the meta line under the name. */
export const tpId = (cxId) => `TP - ${cxId}`;

/** The customer's address as one truncated blue line under the name. */
export const AddressLine = ({ address, style }) =>
  address ? (
    <RText
      size={11}
      weight={500}
      color={C.blue}
      numberOfLines={1}
      ellipsizeMode="tail"
      style={style}
    >
      {address}
    </RText>
  ) : null;
